import { homeSpecimenUploadDialogMessages, SpecimenUploadDialogProps, useSpecimenUploadLogic } from 'azshared';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { AppButton, AppButtonType, AppButtonVariant } from '../../../../../components/buttons/app-button';
import { AppDialog } from '../../../../../components/dialog/app-dialog';
import './specimen-upload-dialog.scss';
import { AppCheckbox } from 'src/components/checkbox/app-checkbox';
import { AppFileInput } from 'src/components/file-input/file-input';
import { AppSvgIcon } from 'src/components/svg-icon/app-svg-icon';
import WebcamCapture from 'src/components/webcam-capture/webcam-capture';

export const SpecimenUploadDialog = (props: SpecimenUploadDialogProps) => {
  const { formatMessage } = useIntl();

  const { open, onClose, onSuccessConfirm } = props;
  const {
    base64Specimen,
    disclaimerAccepted,
    canConfirm,
    showConfirm,
    showImagePreview,
    isSpecimenFromState,
    showRetryAndConfirm,
    showUploadAndTakePhoto,
    cameraOpen,
    onConfirm,
    handleClose,
    uploadSpecimen,
    onDisclaimerCheck,
    deleteSpecimen,
    confirmSpecimen,
    onPhotoTaken,
    setCameraOpen
  } = useSpecimenUploadLogic({
    onClose,
    onSuccessConfirm
  });
  const [isLoading, setIsLoading] = useState(false);

  const onConfirmClick = useCallback(() => {
    setIsLoading(true);
    onConfirm();
  }, [onConfirm, setIsLoading]);

  useEffect(() => {
    if (!open) {
      setIsLoading(false);
    }
  }, [open]);
  const handleSetCameraOpen = React.useCallback(() => {
    setCameraOpen(true);
  }, [setCameraOpen]);

  const handleRetry = React.useCallback(() => {
    deleteSpecimen();
    setCameraOpen(true);
  }, [deleteSpecimen, setCameraOpen]);

  return (
    <AppDialog open={open} onClose={handleClose}>
      <div className='specimen-upload-dialog'>
        <div className='title-section'>
          <div className='title'>{formatMessage(homeSpecimenUploadDialogMessages.cardTitle)}</div>
          <div className='subtitle'>{formatMessage(homeSpecimenUploadDialogMessages.cardSubtitle)}</div>
        </div>

        {showImagePreview && (
          <div className='image-preview-container'>
            {showConfirm && (
              <div className='delete-icon-container'>
                <AppSvgIcon icon='trash-white' className='delete-icon' onClick={deleteSpecimen} />
              </div>
            )}
            <img width='100%' height='100%' className='image-preview' src={base64Specimen} />
          </div>
        )}

        {/* Upload and take photo buttons*/}
        {showUploadAndTakePhoto && (
          <div className='button-section'>
            <AppFileInput
              buttonLabel={formatMessage(homeSpecimenUploadDialogMessages.uploadLabel)}
              onUpload={uploadSpecimen}
              acceptedFile='.png, .jpg, .jpeg'
            />
            <AppButton
              type={AppButtonType.BUTTON}
              variant={AppButtonVariant.Primary}
              label={formatMessage(homeSpecimenUploadDialogMessages.takePhotoLabel)}
              onClick={handleSetCameraOpen}
            />
          </div>
        )}

        {cameraOpen && <WebcamCapture onPhotoTaken={onPhotoTaken} />}

        {/* Retry and confirm buttons */}
        {showRetryAndConfirm && (
          <div className='button-section'>
            <AppButton
              type={AppButtonType.BUTTON}
              variant={AppButtonVariant.Primary}
              label={formatMessage(homeSpecimenUploadDialogMessages.retryLabel)}
              onClick={handleRetry}
            />
            <AppButton
              type={AppButtonType.BUTTON}
              variant={AppButtonVariant.Primary}
              label={formatMessage(homeSpecimenUploadDialogMessages.confirmCTA)}
              onClick={confirmSpecimen}
            />
          </div>
        )}

        {/* Disclaimer checkbox and confirm button */}
        <div className='button-section--confirm'>
          <AppCheckbox
            disabled={!showConfirm || showRetryAndConfirm || isSpecimenFromState}
            label={formatMessage(homeSpecimenUploadDialogMessages.cardDisclaimer)}
            checked={disclaimerAccepted}
            onChange={onDisclaimerCheck}
          />
          <AppButton
            type={AppButtonType.BUTTON}
            variant={AppButtonVariant.Primary}
            loading={isLoading}
            disabled={!canConfirm || isSpecimenFromState}
            label={formatMessage(homeSpecimenUploadDialogMessages.confirmCTA)}
            onClick={onConfirmClick}
          />
        </div>
      </div>
    </AppDialog>
  );
};
